import moment from 'moment';

/**
 * Flatten a nested object into a single-level object.
 * - Recursively spreads nested objects
 * - Formats date strings automatically
 * - Skips arrays (related entities)
 * 
 * @param obj - The object to flatten
 * @returns A single-level object with all nested properties spread
 */
export function flattenObject(obj: any): Record<string, any> {
  const result: Record<string, any> = {};
  
  if (!obj || typeof obj !== 'object') {
    return result;
  }

  for (const key of Object.keys(obj)) {
    const value = obj[key];
    
    // Skip null/undefined
    if (value === null || value === undefined) {
      result[key] = '';
      continue;
    }
    
    // Format date strings (ISO format detection)
    if (typeof value === 'string' && isDateString(value)) {
      result[key] = moment(value).format('DD/MM/YYYY');
      continue;
    }
    
    // Skip arrays (related entities like line items)
    // edit : not skipping arrays to retrieve info in the arrays.
    if (Array.isArray(value)) {
      const flattened = flattenObject(value);
      for (const nestedKey of Object.keys(flattened)) {
        if (!(nestedKey in result)) {
          result[nestedKey] = flattened[nestedKey];
        }
      }
      continue;
    }
    
    // Recursively flatten nested objects (use their keys directly, not prefixed)
    // Only add nested keys if they don't already exist (parent-level keys take priority)
    if (typeof value === 'object') {
      const flattened = flattenObject(value);
      for (const nestedKey of Object.keys(flattened)) {
        if (!(nestedKey in result)) {
          result[nestedKey] = flattened[nestedKey];
        }
      }
      continue;
    }
    
    // Primitive values - use original key
    result[key] = value;
  }
  
  return result;
}

/**
 * Check if a string is an ISO date format
 */
function isDateString(value: string): boolean {
  // Check for ISO date format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(value);
}

/**
 * Extract all keys from the first item of an array.
 * Useful for generating dynamic column definitions.
 * 
 * @param items - Array of data items
 * @returns List of all keys from the first item (flattened)
 */
export function extractKeysFromData(items: any[]): string[] {
  if (!items || items.length === 0) {
    return [];
  }
  
  const flattened = flattenObject(items[0]);
  return Object.keys(flattened);
}
